import { Box3, Vector3 } from "three";

class VoxelGridCalculator {
    private static findBestGridSizeScalar(
        bbx: number, bby: number, bbz: number,
        padding: number, minDimSize: number, targetVoxNum: number
    ) {
        const h = Math.cbrt((bbx * bby * bbz) / targetVoxNum);

        let gx = Math.round(bbx / h);
        let gy = Math.round(bby / h);
        let gz = Math.round(bbz / h);

        const vx = bbx / gx;
        const vy = bby / gy;
        const vz = bbz / gz;

        const paddingAdjustment = (dim: number, size: number) => {
            return Math.max(2 * padding, minDimSize - dim);
        };

        const px = paddingAdjustment(gx, minDimSize);
        const py = paddingAdjustment(gy, minDimSize);
        const pz = paddingAdjustment(gz, minDimSize);

        const nbbx = bbx + vx * px;
        const nbby = bby + vy * py;
        const nbbz = bbz + vz * pz;

        gx += px;
        gy += py;
        gz += pz;

        return {
            voxNum: gx * gy * gz,
            gx, gy, gz,
            nbbx, nbby, nbbz
        };
    }

    private static findBestGridSizeVector3(
        bbx: number, bby: number, bbz: number,
        padding: number, minDimSize: number, targetVoxNum: number
    ) {
        const VOX_NUM_TOL = 0.05;
        const maxVoxNum = targetVoxNum * (1 + VOX_NUM_TOL);

        let l = 1;
        let r = targetVoxNum;
        let bestResult: any = null;
        let error = -1;

        while (l < r - 1) {
            const mid = (l + r) / 2;
            const result = this.findBestGridSizeScalar(bbx, bby, bbz, padding, minDimSize, mid);
            const voxelsNum = result.voxNum;

            if (voxelsNum > targetVoxNum) {
                r = mid;
            } else {
                l = mid;
            }

            if (!bestResult || Math.abs(voxelsNum - targetVoxNum) < Math.abs(bestResult.voxNum - targetVoxNum)) {
                bestResult = result;
            }
        }

        for (let i = l; i < r; i++) {
            const result = this.findBestGridSizeScalar(bbx, bby, bbz, padding, minDimSize, i);
            const voxelsNum = result.voxNum;

            if (voxelsNum > maxVoxNum) {
                break;
            }

            const vx = result.nbbx / result.gx;
            const vy = result.nbby / result.gy;
            const vz = result.nbbz / result.gz;

            const currentError = Math.max(Math.abs(vx - vy), Math.max(Math.abs(vx - vz), Math.abs(vy - vz)));

            if (i === l || currentError < error) {
                error = currentError;
                bestResult = result;
            }
        }

        return {
            vec: new Vector3(bestResult.gx + 1, bestResult.gy + 1, bestResult.gz + 1),
            nnbbx: bestResult.nbbx,
            nnbby: bestResult.nbby,
            nnbbz: bestResult.nbbz
        };
    }

    private static getVoxelSize(meshesBBox: Box3, padding: number, desiredVoxelsNum: number) {
        const MIN_DIM_SIZE = 16;

        const bbx = meshesBBox.max.x - meshesBBox.min.x;
        const bby = meshesBBox.max.y - meshesBBox.min.y;
        const bbz = meshesBBox.max.z - meshesBBox.min.z;

        const result = this.findBestGridSizeVector3(bbx, bby, bbz, padding, MIN_DIM_SIZE, desiredVoxelsNum);
        const gridSize = result.vec;

        const voxelSizeX = result.nnbbx / gridSize.x;
        const voxelSizeY = result.nnbby / gridSize.y;
        const voxelSizeZ = result.nnbbz / gridSize.z;

        return new Vector3(voxelSizeX, voxelSizeY, voxelSizeZ);
    }

    public static getFeatureSize(meshesBBox: Box3, padding: number, desiredVoxelsNum: number) {
        const voxelSize = this.getVoxelSize(meshesBBox, padding, desiredVoxelsNum);
        const featureSize = voxelSize.length() / Math.sqrt(3) * 3;
        return Number.parseFloat(featureSize.toFixed(2));
    }
}

export { VoxelGridCalculator };